// ─── Which dashboard row a saved pick refers to ───────────────────────────────
// Pure, no React — used by components/DashboardPicker.jsx and tested directly.
//
// A dashboardId does NOT identify one row in the picker. The same dashboard is
// listed under every EVENT that uses it (a shared template, or a per-event fork),
// because which event you read it under is what decides the data — applyScope
// forces that event's lock onto the query. So "Live Attendance Dashboard" appears
// once under DJ Kent and once under Liquideep, same id, different suiteId.
//
// Two things follow, and both were got wrong:
//
//   1. Choosing a row has to carry the ROW, not just its id. The conversion-source
//      pickers took the id back and re-found it with `.find()`, which returns the
//      FIRST event that has that dashboard — so picking the Liquideep row saved DJ
//      Kent's suiteId, and the campaign then counted DJ Kent's attendance list.
//   2. Showing a saved pick back has to prefer the event it was SAVED under. The
//      conversion block preferred the CAMPAIGN's event instead, so the control
//      re-rendered on DJ Kent the instant you clicked Liquideep — the "it keeps
//      reverting" (2026-09-03).

// The row a saved { dashboardId, suiteId } refers to, out of the flat list.
// `variantOf` follows a fork: customising a dashboard for a client mints a new id,
// and a picker that answers "nothing selected" to a perfectly good saved source is
// how a working campaign starts looking broken (server/dashRef.js does the same
// when the source is actually read).
export function pickSelected(rows, value, preferSuiteId) {
  const hits = (rows || []).filter((d) => d && (d.dashboardId === value || (value && d.variantOf === value)));
  if (!hits.length) return null;
  // The saved event wins. Falling back to the first hit is a guess, and it is only
  // ever right by luck — but it beats showing nothing when the event is gone.
  return (preferSuiteId && hits.find((d) => d.suiteId === preferSuiteId)) || hits[0];
}

// Is this pick ambiguous — the same dashboard offered under more than one event?
// True means an id alone cannot say which one, so the caller must keep the row's
// suiteId or it will silently read another event's numbers.
export function isAmbiguous(rows, value) {
  return (rows || []).filter((d) => d && d.dashboardId === value).length > 1;
}
